/**
 * Buy X Get Y Cart Offer Manager Component for Focal Theme
 * Automatically adds free product Y when customer purchases any product X
 * Version: 2.0.0
 * 
 * Usage:
 * <buy-x-get-y-offer-manager>
 *   <script type="application/json">
 *     // Offer configuration from metaobjects
 *   </script>
 * </buy-x-get-y-offer-manager>
 */

// Global state for Buy X Get Y offers
window.BuyXGetYGlobal = window.BuyXGetYGlobal || {
  isProcessing: false,
  lastProcessTime: 0,
  isRefreshing: false,
  loadingVisible: false
};

class BuyXGetYOfferManager extends HTMLElement {
  constructor() {
    super();
    
    this.THROTTLE_DELAY = 500;
    this.offerConfig = null;
    this.OFFER_PROPERTY_KEY = '_BuyXGetY';
    
    // Bind methods
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    console.log('[BuyXGetY] 🎯 Component connected');
    
    // Ensure loading indicator exists
    this.ensureLoadingIndicator();
    
    // Parse offer configuration
    this.parseOfferConfig();
    
    if (!this.offerConfig) {
      console.warn('[BuyXGetY] ⚠️ No offer configured');
      return;
    }

    console.log('[BuyXGetY] 📋 Offer configuration:', {
      name: this.offerConfig.offer_name,
      xProducts: this.offerConfig.product_x_variants?.length,
      yProduct: this.offerConfig.product_y_variant,
      enabled: this.offerConfig.enable
    });

    // Listen to cart updates
    document.documentElement.addEventListener('cart:updated', this.handleCartUpdate);
    
    console.log('[BuyXGetY] ✓ Event listener attached');
    
    // Initial check with proper DOM/cart readiness check
    this.safeInitialize();
  }

  disconnectedCallback() {
    document.documentElement.removeEventListener('cart:updated', this.handleCartUpdate);
    console.log('[BuyXGetY] Component disconnected');
  }

  /**
   * Parse offer configuration from JSON script tag
   */
  parseOfferConfig() {
    const configScript = this.querySelector('script[type="application/json"]');
    
    if (!configScript) {
      console.error('[BuyXGetY] ❌ No config script found');
      return;
    }

    try {
      this.offerConfig = JSON.parse(configScript.textContent);
      console.log('[BuyXGetY] ✅ Config loaded:', this.offerConfig);
    } catch (e) {
      console.error('[BuyXGetY] ❌ Failed to parse config:', e);
    }
  }

  /**
   * Safe initialization with DOM and cart readiness checks
   */
  safeInitialize() {
    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      console.log('[BuyXGetY] ⏳ Waiting for DOM to be ready...');
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeWithRetry();
      });
    } else {
      // DOM already ready, initialize with retry logic
      this.initializeWithRetry();
    }
  }

  /**
   * Initialize with retry logic for first page load
   */
  async initializeWithRetry(attempt = 1, maxAttempts = 3) {
    const delay = attempt === 1 ? 500 : 1000; // 500ms first try, 1s for retries
    
    console.log(`[BuyXGetY] 🔄 Initialize attempt ${attempt}/${maxAttempts} (delay: ${delay}ms)`);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      
      // Verify cart is valid
      if (!cart || typeof cart.items === 'undefined') {
        throw new Error('Invalid cart data');
      }
      
      console.log('[BuyXGetY] ✅ Cart data loaded successfully');
      await this.processOffer(cart);
      
    } catch (error) {
      console.error(`[BuyXGetY] ❌ Error on attempt ${attempt}:`, error);
      
      // Retry if we haven't exceeded max attempts
      if (attempt < maxAttempts) {
        console.log(`[BuyXGetY] 🔄 Retrying... (${attempt + 1}/${maxAttempts})`);
        await this.initializeWithRetry(attempt + 1, maxAttempts);
      } else {
        console.error('[BuyXGetY] ❌ Failed to initialize after all attempts');
      }
    }
  }

  /**
   * Initialize with current cart state (legacy - kept for compatibility)
   */
  async initializeWithCurrentCart() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      await this.processOffer(cart);
    } catch (error) {
      console.error('[BuyXGetY] ❌ Error initializing:', error);
    }
  }

  /**
   * Handle cart update events
   */
  async handleCartUpdate(event) {
    const global = window.BuyXGetYGlobal;
    const now = Date.now();
    
    // Throttle processing
    if (now - global.lastProcessTime < this.THROTTLE_DELAY) {
      console.log('[BuyXGetY] ⏭️ Throttling...');
      return;
    }
    
    if (global.isProcessing) {
      console.log('[BuyXGetY] ⏭️ Already processing...');
      return;
    }

    global.lastProcessTime = now;
    
    const cart = event.detail?.cart;
    if (!cart) {
      console.warn('[BuyXGetY] ⚠️ No cart data in event');
      return;
    }
    
    await this.processOffer(cart);
  }

  /**
   * Main offer processing logic
   */
  async processOffer(cart) {
    const global = window.BuyXGetYGlobal;
    
    if (global.isProcessing) {
      return;
    }

    if (!this.offerConfig || !this.offerConfig.enable) {
      this.hideOfferUI();
      return;
    }

    // Show loading IMMEDIATELY
    this.showLoading();
    
    global.isProcessing = true;
    console.log('[BuyXGetY] 🔄 Processing offer...');

    try {
      const xVariants = this.offerConfig.product_x_variants || [];
      const yVariant = this.offerConfig.product_y_variant;

      if (!yVariant || xVariants.length === 0) {
        console.warn('[BuyXGetY] ⚠️ Invalid config');
        this.hideOfferUI();
        this.hideLoading();
        return;
      }

      // Check if any X product is in cart
      const hasXInCart = this.hasAnyXProductInCart(cart, xVariants);
      
      // Check if Y product exists in cart (with our property)
      const yInCart = this.getYProductInCart(cart, yVariant);

      console.log('[BuyXGetY] 📊 Cart status:', {
        hasXInCart,
        yInCart: !!yInCart
      });

      // Decision logic
      if (hasXInCart) {
        // Show active UI
        this.showOfferUI(true, true);
        
        if (!yInCart) {
          // Add Y product
          console.log('[BuyXGetY] ➕ Adding Y product');
          
          // Set refreshing flag BEFORE adding
          global.isRefreshing = true;
          
          await this.addYProduct(yVariant);
          return;
        } else if (yInCart.quantity !== 1) {
          // Fix quantity (should always be 1)
          console.log('[BuyXGetY] 🔧 Fixing Y product quantity');
          
          global.isRefreshing = true;
          
          await this.fixYProductQuantity(cart, yVariant);
          return;
        }
      } else {
        // Show inactive UI
        this.showOfferUI(true, false);
        
        if (yInCart) {
          // Remove Y product
          console.log('[BuyXGetY] 🗑️ Removing Y product');
          
          global.isRefreshing = true;
          
          await this.removeYProduct(cart, yVariant);
          return;
        }
      }

      // Clear refresh flag if no cart modification
      global.isRefreshing = false;
      
      // Hide loading after UI update
      setTimeout(() => this.hideLoading(), 300);

    } catch (error) {
      console.error('[BuyXGetY] ❌ Error processing offer:', error);
      this.hideLoading();
    } finally {
      global.isProcessing = false;
      console.log('[BuyXGetY] ✅ Processing complete');
    }
  }

  /**
   * Check if any X product variant is in cart
   */
  hasAnyXProductInCart(cart, xVariants) {
    if (!cart.items || !Array.isArray(cart.items) || !xVariants || xVariants.length === 0) {
      return false;
    }

    const xVariantNumbers = xVariants.map(id => Number(id));
    
    return cart.items.some(item => {
      const itemVariantId = Number(item.variant_id);
      // Don't count Y products that have our offer property
      const isYProduct = item.properties && item.properties[this.OFFER_PROPERTY_KEY];
      return xVariantNumbers.includes(itemVariantId) && !isYProduct;
    });
  }

  /**
   * Get Y product from cart (must have offer property)
   */
  getYProductInCart(cart, yVariant) {
    if (!cart.items || !Array.isArray(cart.items)) {
      return null;
    }

    return cart.items.find(item => {
      const matchesVariant = Number(item.variant_id) === Number(yVariant);
      const hasOfferProperty = item.properties && item.properties[this.OFFER_PROPERTY_KEY];
      return matchesVariant && hasOfferProperty;
    });
  }

  /**
   * Add Y product to cart
   */
  async addYProduct(yVariant) {
    try {
      console.log('[BuyXGetY] 📡 Adding Y product:', yVariant);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: yVariant,
            quantity: 1,
            properties: {
              [this.OFFER_PROPERTY_KEY]: 'true'
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add Y product');
      }

      console.log('[BuyXGetY] ✅ Y product added');
      
      // Refresh cart UI
      await this.refreshCartUI();
      
    } catch (error) {
      console.error('[BuyXGetY] ❌ Error adding Y product:', error);
      this.hideLoading();
    }
  }

  /**
   * Remove Y product from cart
   */
  async removeYProduct(cart, yVariant) {
    try {
      const yItem = this.getYProductInCart(cart, yVariant);

      if (!yItem) {
        console.log('[BuyXGetY] ℹ️ No Y product to remove');
        return;
      }

      console.log('[BuyXGetY] 📡 Removing Y product:', yItem.key);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: yItem.key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove Y product');
      }

      console.log('[BuyXGetY] ✅ Y product removed');
      
      // Refresh cart UI
      await this.refreshCartUI();
      
    } catch (error) {
      console.error('[BuyXGetY] ❌ Error removing Y product:', error);
      this.hideLoading();
    }
  }

  /**
   * Fix Y product quantity (should always be 1)
   */
  async fixYProductQuantity(cart, yVariant) {
    try {
      const yItem = this.getYProductInCart(cart, yVariant);

      if (!yItem) {
        return;
      }

      console.log('[BuyXGetY] 📡 Fixing Y product quantity:', yItem.key);

      // Update quantity to 1
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: yItem.key,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fix Y product quantity');
      }

      console.log('[BuyXGetY] ✅ Y product quantity fixed');
      
      // Refresh cart UI
      await this.refreshCartUI();
      
    } catch (error) {
      console.error('[BuyXGetY] ❌ Error fixing Y product quantity:', error);
      this.hideLoading();
    }
  }

  /**
   * Refresh cart UI
   */
  async refreshCartUI() {
    try {
      const global = window.BuyXGetYGlobal;
      
      // Flag should already be set by caller
      if (!global.isRefreshing) {
        global.isRefreshing = true;
        console.log('[BuyXGetY] 🔄 Setting refresh flag (fallback)');
      }
      
      const cart = await fetch('/cart.js').then(r => r.json());
      
      if (window.location.href.indexOf('cart') > -1) {
        window.location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cart,
            openMiniCart: true,
            openDrawer: false
          }
        }));
        
        // Clear refresh flag after cart drawer has re-rendered
        setTimeout(() => {
          global.isRefreshing = false;
          this.hideLoading();
          console.log('[BuyXGetY] ✅ Clearing refresh flag');
        }, 1000);
      }
    } catch (error) {
      console.error('[BuyXGetY] ❌ Error refreshing cart:', error);
      window.BuyXGetYGlobal.isRefreshing = false;
      this.hideLoading();
    }
  }

  /**
   * Show/hide offer UI based on state
   */
  showOfferUI(show, hasXInCart) {
    const offerContainer = this.querySelector('[data-offer-ui]');
    const preMessageEl = this.querySelector('[data-pre-message]');
    const postMessageEl = this.querySelector('[data-post-message]');
    
    if (!offerContainer) {
      return;
    }

    // Check if we have any messages to display
    const hasAnyMessage = (this.offerConfig.pre_message && this.offerConfig.pre_message.trim()) || 
                          (this.offerConfig.post_message && this.offerConfig.post_message.trim());

    if (!show || !hasAnyMessage) {
      offerContainer.style.display = 'none';
      if (preMessageEl) preMessageEl.style.display = 'none';
      if (postMessageEl) postMessageEl.style.display = 'none';
      return;
    }

    // Show the container
    offerContainer.style.display = 'block';
    
    // Show appropriate message
    if (hasXInCart) {
      // X in cart - Show POST message
      if (preMessageEl) preMessageEl.style.display = 'none';
      if (postMessageEl) postMessageEl.style.display = 'block';
      offerContainer.classList.add('offer--active');
      offerContainer.classList.remove('offer--inactive');
    } else {
      // No X in cart - Show PRE message
      if (preMessageEl) preMessageEl.style.display = 'block';
      if (postMessageEl) postMessageEl.style.display = 'none';
      offerContainer.classList.add('offer--inactive');
      offerContainer.classList.remove('offer--active');
    }
  }

  /**
   * Hide offer UI
   */
  hideOfferUI() {
    const offerContainer = this.querySelector('[data-offer-ui]');
    if (offerContainer) {
      offerContainer.style.display = 'none';
    }
  }

  /**
   * Ensure loading indicator exists in DOM
   */
  ensureLoadingIndicator() {
    if (document.getElementById('buy-x-get-y-loading')) {
      return;
    }

    console.log('[BuyXGetY] 🎨 Creating loading indicator');

    const loadingDiv = document.createElement('div');
    loadingDiv.id = 'buy-x-get-y-loading';
    loadingDiv.style.display = 'none';
    loadingDiv.innerHTML = `
      <div class="buy-x-get-y-loader">
        <div class="buy-x-get-y-loader-content">
          <svg class="spinner" width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="2"></circle>
          </svg>
          <p class="buy-x-get-y-loader-text">Updating cart...</p>
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      #buy-x-get-y-loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        border-radius: inherit;
      }

      .buy-x-get-y-loader {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .buy-x-get-y-loader-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        background: white;
        padding: 24px 32px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .buy-x-get-y-loader-text {
        margin: 0;
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }

      .spinner {
        animation: rotate 1s linear infinite;
      }

      .spinner-circle {
        stroke: #3498db;
        stroke-linecap: round;
        animation: dash 1.5s ease-in-out infinite;
      }

      @keyframes rotate {
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes dash {
        0% {
          stroke-dasharray: 1, 150;
          stroke-dashoffset: 0;
        }
        50% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -35;
        }
        100% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -124;
        }
      }
    `;

    // Append to cart drawer
    const cartDrawer = document.getElementById('mini-cart');
    if (cartDrawer) {
      if (getComputedStyle(cartDrawer).position === 'static') {
        cartDrawer.style.position = 'relative';
      }
      cartDrawer.appendChild(loadingDiv);
      cartDrawer.appendChild(style);
      console.log('[BuyXGetY] ✅ Loading indicator appended to cart drawer');
    } else {
      document.body.appendChild(loadingDiv);
      document.body.appendChild(style);
      console.log('[BuyXGetY] ⚠️ Cart drawer not found, appended to body');
    }
  }

  /**
   * Show loading indicator
   */
  showLoading() {
    const global = window.BuyXGetYGlobal;
    
    if (global.loadingVisible) {
      return;
    }

    const loader = document.getElementById('buy-x-get-y-loading');
    if (loader) {
      loader.style.display = 'flex';
      global.loadingVisible = true;
      console.log('[BuyXGetY] 👁️ Loading visible');
    }
  }

  /**
   * Hide loading indicator
   */
  hideLoading() {
    const global = window.BuyXGetYGlobal;
    const loader = document.getElementById('buy-x-get-y-loading');
    
    if (loader && global.loadingVisible) {
      loader.style.display = 'none';
      global.loadingVisible = false;
      console.log('[BuyXGetY] 👁️ Loading hidden');
    }
  }
}

// Register custom element
if (!customElements.get('buy-x-get-y-offer-manager')) {
  customElements.define('buy-x-get-y-offer-manager', BuyXGetYOfferManager);
  console.log('[BuyXGetY] 🎨 Component registered ✓');
}
